'use client';
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { PAL_INDEX as PALS } from "~/lib/pals-index";
import { useBreeding } from "./breeding-store";

function QuickJump() {
  const router = useRouter();
  const ref = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return PALS.filter((p) => p.name.toLowerCase().includes(s) || p.number.toLowerCase().startsWith(s)).slice(0, 7);
  }, [q]);
  const go = (key: string) => {
    router.push(`/pals/${key}`);
    setQ("");
    setOpen(false);
    ref.current?.blur();
  };
  return (
    <div className="relative ml-auto hidden w-64 laptop:block">
      <input
        ref={ref}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter") { const m = matches[active]; if (m) go(m.key); }
          if (e.key === "Escape") ref.current?.blur();
        }}
        placeholder="Jump to a Pal…"
        aria-label="Jump to a Pal"
        className="h-9 w-full rounded-md border border-deck-line bg-deck-bg pl-3 pr-14 text-sm text-deck-text placeholder:text-deck-muted focus:border-deck-cyan focus:outline-none"
      />
      <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-deck-line bg-deck-card px-1.5 font-mono text-[10px] text-deck-muted">Ctrl K</kbd>
      {open && matches.length > 0 && (
        <ul className="absolute right-0 z-50 mt-1 w-full overflow-hidden rounded-lg border border-deck-line bg-deck-card shadow-2xl">
          {matches.map((p, i) => (
            <li key={p.id}>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(p.key)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${i === active ? "bg-deck-raised text-deck-cyan" : "text-deck-text"}`}
              >
                <Image src={p.image} width={24} height={24} alt="" className="rounded-full" />
                <span className="font-mono text-xs text-deck-muted">#{p.number}</span>
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const LINKS = [
  { href: "/", label: "Paldeck" },
  { href: "/breeding", label: "Breeding" },
  { href: "/items", label: "Items" },
  { href: "/skills", label: "Skills" },
  { href: "/types", label: "Work & Types" },
  { href: "/guides", label: "Guides" },
  { href: "/tools/server-settings", label: "Server" },
];

export default function SiteNav() {
  const path = usePathname();
  const [open, setOpen] = useState(false);
  const { pool, target } = useBreeding();
  const active = (href: string) => (href === "/" ? path === "/" || path.startsWith("/pals") : path.startsWith(href));
  const breedCount = pool.length + (target ? 1 : 0);

  return (
    <header className="sticky top-0 z-40 border-b border-deck-line bg-deck-bg/90 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-6 px-4">
        <Link href="/" className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight text-deck-text">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-deck-cyan font-mono text-sm font-bold text-deck-bg">P</span>
          Pal<span className="text-deck-cyan">Deck</span>
        </Link>
        <nav className="hidden gap-1 tablet:flex">
          {LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className={`relative rounded-md px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                active(l.href) ? "bg-deck-raised text-deck-cyan" : "text-deck-muted hover:text-deck-text"
              }`}
            >
              {l.label}
              {l.href === "/breeding" && breedCount > 0 && (
                <span className="ml-1.5 rounded bg-deck-green px-1 text-[10px] font-bold text-deck-bg">{breedCount}</span>
              )}
            </Link>
          ))}
        </nav>
        <QuickJump />
        <button
          className="ml-auto rounded-md border border-deck-line px-3 py-1 font-mono text-xs text-deck-muted tablet:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          Menu
        </button>
      </div>
      {open && (
        <nav className="flex flex-col border-t border-deck-line px-4 py-2 tablet:hidden">
          {LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className={`py-2 font-mono text-sm uppercase tracking-wider ${active(l.href) ? "text-deck-cyan" : "text-deck-muted"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
